import React, { useState, useRef, useEffect } from 'react';
import { FaStepBackward, FaStepForward, FaPlay, FaPause, FaRandom, FaVolumeDown, FaVolumeUp } from 'react-icons/fa';
import './MusicPlayer.css';
import { loadPlaylists, loadTrackArtwork } from './musicLoader';
import ScrollingLabel from './ScrollingLabel';
import butterchurn from 'butterchurn';
import butterchurnPresets from 'butterchurn-presets';

// Tap the visualizer to cycle through these, in order. Hand-picked by
// the team from the main pack's 100 presets (reviewed via the old
// numbered-badge rotation) — indices below are the 1-based numbers
// they called out, #61 (Jacob Horne's pick) included.
//
// butterchurn-presets also ships 5 more packs (Extra, Extra2, MD1,
// Minimal, NonMinimal — ~455 more presets) as separate bundle files
// (e.g. butterchurn-presets/lib/butterchurnPresetsExtra.min.js), each
// independently `import()`-able. None of that is wired up yet — adding
// presets from those packs to this rotation later means dynamically
// importing the specific pack the first time one of its presets is
// selected (and caching the loaded module), so nothing downloads or
// costs anything until a tap actually reaches it.
const ALL_MAIN_PACK_PRESETS = Object.keys(butterchurnPresets.getPresets());
const VISUALIZER_WHITELIST_NUMBERS = [3, 11, 15, 16, 17, 18, 33, 42, 57, 58, 61];
const VISUALIZER_PRESETS = VISUALIZER_WHITELIST_NUMBERS.map(n => ALL_MAIN_PACK_PRESETS[n - 1]);

// Seconds to crossfade when cycling to a new preset via tap. (The
// initial load-in uses a longer blend — see the mount effect below.)
const PRESET_CYCLE_BLEND_SECONDS = 2.5;

// requestIdleCallback isn't supported on Safari/iOS — fall back to a
// short setTimeout there. Used to pre-warm visualizer presets (see
// below) without competing with anything actually time-sensitive.
function scheduleIdle(fn) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    return { kind: 'idle', id: window.requestIdleCallback(fn, { timeout: 2000 }) };
  }
  return { kind: 'timeout', id: setTimeout(fn, 200) };
}

function cancelIdle(handle) {
  if (!handle) return;
  if (handle.kind === 'idle' && typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(handle.id);
  } else if (handle.kind === 'timeout') {
    clearTimeout(handle.id);
  }
}

const MusicPlayer = () => {
  // Default album art if none is provided
  const defaultAlbumArt = 'https://placehold.co/600x600?text=?';

  const [playlists, setPlaylists] = useState([]);

  useEffect(() => {
    loadPlaylists().then(setPlaylists);
  }, []);

  // currentPlaylistIndex/currentSongIndex are what's cued/playing.
  // viewedPlaylistIndex is just which playlist's tracks are shown in the
  // list — browsing a different playlist doesn't interrupt playback.
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0);
  const [viewedPlaylistIndex, setViewedPlaylistIndex] = useState(0);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // Progress as a percentage
  const [shuffle, setShuffle] = useState(true);
  const [volume, setVolume] = useState(1.0);
  const [playOrder, setPlayOrder] = useState([]);
  const [playOrderIndex, setPlayOrderIndex] = useState(0);
  const audioRef = useRef(null);
  const loadedSrcRef = useRef(null);

  const currentTracks = playlists[currentPlaylistIndex]?.tracks || [];
  const viewedTracks = playlists[viewedPlaylistIndex]?.tracks || [];

  // Set init UI to use before songs have loaded
  let currentSong = currentTracks[currentSongIndex];

  if (!currentSong) {
    currentSong = {
      title: "",
      artist: "",
      album: "",
    }
  }

  // butterchurn
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const gainNodeRef = useRef(null);
  const visualizerCanvasRef = useRef(null);
  const visualizerRef = useRef(null);
  const activePresetRef = useRef(null);
  // Start on a random preset from the rotation each time the player
  // mounts, rather than always opening on the same one.
  const [presetIndex, setPresetIndex] = useState(
    () => Math.floor(Math.random() * VISUALIZER_PRESETS.length)
  );

  // Pending teardown timer — see the cleanup at the bottom of this effect.
  const teardownTimerRef = useRef(null);

  useEffect(() => {
    // bail until the <audio> is in the DOM
    if (!audioRef.current) return;

    const audioEl = audioRef.current;
    const canvasEl = visualizerCanvasRef.current;

    // React StrictMode (dev only) runs this effect, its cleanup, then the
    // effect again on the same instance. The cleanup defers the real
    // teardown by a tick, so a re-run lands here first and cancels it —
    // keeping the existing audio graph. (It has to: an <audio> element can
    // only ever be wired to one MediaElementSourceNode, so it can't be
    // torn down and rebuilt.)
    if (teardownTimerRef.current) {
      clearTimeout(teardownTimerRef.current);
      teardownTimerRef.current = null;
    }

    if (!audioContextRef.current) {
      // 1) create the AudioContext
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioCtx();

      // 2) hook up the MediaElementSource
      sourceNodeRef.current =
        audioContextRef.current.createMediaElementSource(audioEl);

      // 3) route through a GainNode for volume control. iOS Safari ignores
      // HTMLMediaElement.volume entirely (volume there can only be changed
      // with the hardware buttons), but it does honor Web Audio gain
      // changes — see the volume effect below.
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.value = volume;

      const visualizer = butterchurn.createVisualizer(audioContextRef.current, canvasEl, {
        width: 640,
        height: 360
      });
      visualizerRef.current = visualizer;

      visualizer.connectAudio(sourceNodeRef.current);
      sourceNodeRef.current.connect(gainNodeRef.current);
      gainNodeRef.current.connect(audioContextRef.current.destination);

      const presets = butterchurnPresets.getPresets();
      const preset = presets[VISUALIZER_PRESETS[presetIndex]];
      activePresetRef.current = preset;

      visualizer.loadPreset(preset, 5.0); // 2nd argument is the number of seconds to blend presets

      visualizer.setRendererSize(640, 360);
    }

    let rafId = null;
    const renderLoop = () => {
      visualizerRef.current.render();
      rafId = requestAnimationFrame(renderLoop);
    };
    renderLoop();

    // Closing the J2nZ window unmounts this component. Without this, every
    // close/reopen left the old render loop, AudioContext and WebGL
    // context running in the background (browsers cap how many of each a
    // page can hold).
    return () => {
      cancelAnimationFrame(rafId);
      teardownTimerRef.current = setTimeout(() => {
        teardownTimerRef.current = null;
        audioEl.pause();
        sourceNodeRef.current?.disconnect();
        gainNodeRef.current?.disconnect();
        audioContextRef.current?.close().catch(() => { });
        // Free the GPU context now rather than whenever GC gets to it.
        canvasEl
          ?.getContext('webgl2')
          ?.getExtension('WEBGL_lose_context')
          ?.loseContext();
        audioContextRef.current = null;
        sourceNodeRef.current = null;
        gainNodeRef.current = null;
        visualizerRef.current = null;
      }, 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cycling to a new preset (tap on the visualizer) just swaps what's
  // loaded into the already-running visualizer — it does NOT recreate
  // it, so this is a separate, much lighter effect from the mount effect
  // above. Skips its first run since the mount effect already loaded
  // the randomized starting preset (see presetIndex's initializer).
  const isFirstPresetRender = useRef(true);
  useEffect(() => {
    if (isFirstPresetRender.current) {
      isFirstPresetRender.current = false;
      return;
    }
    const visualizer = visualizerRef.current;
    if (!visualizer) return;
    const presets = butterchurnPresets.getPresets();
    const preset = presets[VISUALIZER_PRESETS[presetIndex]];
    if (preset) {
      activePresetRef.current = preset;
      visualizer.loadPreset(preset, PRESET_CYCLE_BLEND_SECONDS);
    }
  }, [presetIndex]);

  // Pre-warm the other rotation presets' WebGL shaders during idle time,
  // right after the visualizer starts up. loadPreset() always recompiles
  // a preset's shaders from scratch — butterchurn caches nothing itself
  // — but browsers' WebGL stacks generally cache compiled shader
  // binaries by source text at the driver level (confirmed: revisiting
  // an already-shown preset doesn't re-stutter). So compiling each
  // preset once here, invisibly, means a later tap onto it hits that
  // driver cache instead of paying the compile cost live, which is what
  // was causing the visible freeze on a preset's first appearance.
  //
  // Each step loads the preset being warmed (compiling it) and then
  // immediately loads whatever's actually on screen back on top of it —
  // both calls happen synchronously, before the render loop's next
  // frame, so nothing visibly flashes. Spread across idle callbacks
  // (one preset per callback) rather than one big loop, so this never
  // competes with anything the user is actually doing.
  useEffect(() => {
    const visualizer = visualizerRef.current;
    if (!visualizer) return;

    let cancelled = false;
    let handle = null;
    const presets = butterchurnPresets.getPresets();
    // The starting preset (whichever random index was picked) is already
    // loaded -- warm the rest of the rotation.
    const toWarm = VISUALIZER_PRESETS.filter((_, i) => i !== presetIndex);

    const warmNext = (i) => {
      if (cancelled) return;
      if (i >= toWarm.length) return;
      const preset = presets[toWarm[i]];
      if (preset) {
        visualizer.loadPreset(preset, 0);
        visualizer.loadPreset(activePresetRef.current, 0);
      }
      handle = scheduleIdle(() => warmNext(i + 1));
    };

    handle = scheduleIdle(() => warmNext(0));

    return () => {
      cancelled = true;
      cancelIdle(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cycleVisualizer = () => {
    setPresetIndex((i) => (i + 1) % VISUALIZER_PRESETS.length);
  };

  const togglePlayPause = () => setIsPlaying(p => !p);

  const playNext = () => {
    if (!currentTracks.length) return;

    if (shuffle && playOrder.length) {
      const nextIndex = (playOrderIndex + 1) % playOrder.length;
      setPlayOrderIndex(nextIndex);
      setCurrentSongIndex(playOrder[nextIndex]);
    } else {
      const next = (currentSongIndex + 1) % currentTracks.length;
      setCurrentSongIndex(next);
    }
    setIsPlaying(true);
  };

  const playPrevious = () => {
    if (!currentTracks.length) return;

    if (shuffle && playOrder.length) {
      const prevIndex = (playOrderIndex - 1 + playOrder.length) % playOrder.length;
      setPlayOrderIndex(prevIndex);
      setCurrentSongIndex(playOrder[prevIndex]);
    } else {
      const prev = (currentSongIndex - 1 + currentTracks.length) % currentTracks.length;
      setCurrentSongIndex(prev);
    }
    setIsPlaying(true);
  };

  // Switch which playlist is playing (called when a track in the viewed
  // playlist is clicked). Playback keeps whatever isPlaying already was —
  // clicking a track cues it, it only auto-plays if something was already
  // playing.
  const playTrackInViewedPlaylist = (index) => {
    if (viewedPlaylistIndex === currentPlaylistIndex) {
      // Same playlist: move the play-order cursor to the clicked track, so
      // Next/Previous continue from here instead of from wherever the
      // cursor was before. (Switching playlists rebuilds playOrder in the
      // effect below, starting from the clicked track.)
      setPlayOrderIndex(Math.max(0, playOrder.indexOf(index)));
    }
    setCurrentPlaylistIndex(viewedPlaylistIndex);
    setCurrentSongIndex(index);
  };

  // Update progress as the song plays
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      if (duration > 0) {
        setProgress((current / duration) * 100);
      }
    }
  };

  // Whenever volume changes, update the GainNode (not audio.volume — see
  // the note where the GainNode is created above).
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = volume;
    }
  }, [volume]);

  const fill = `${volume * 100}%`;

  // Allow user to click on the progress bar to seek
  const handleProgressClick = (e) => {
    const containerWidth = e.currentTarget.clientWidth;
    const clickX = e.nativeEvent.offsetX;
    const newProgress = clickX / containerWidth;
    if (audioRef.current && audioRef.current.duration) {
      audioRef.current.currentTime = newProgress * audioRef.current.duration;
    }
  };

  // Automatically play the next song when the current one ends
  const handleEnded = () => {
    playNext();
  };

  // Set playOrder — scoped to whichever playlist is currently cued/playing.
  // With shuffle on, the current track goes first and the rest are shuffled
  // behind it, so Next never repeats a track until the playlist has cycled.
  // On the very first load there's no "current track" yet, so it starts on
  // a random one instead of always cueing track 1 of playlist 1.
  const hasInitialOrderRef = useRef(false);
  useEffect(() => {
    if (!currentTracks.length) return;
    const indices = currentTracks.map((_, i) => i);
    if (shuffle) {
      // Fisher–Yates shuffle
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      let order = indices;
      if (hasInitialOrderRef.current) {
        order = [currentSongIndex, ...indices.filter(i => i !== currentSongIndex)];
      } else {
        setCurrentSongIndex(order[0]);
      }
      setPlayOrder(order);
      setPlayOrderIndex(0);
    } else {
      // shuffle off: linear order, picking up from the current track
      setPlayOrder(indices);
      setPlayOrderIndex(currentSongIndex);
    }
    hasInitialOrderRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlaylistIndex, playlists, shuffle]);

  // Play/Pause effect. This used to depend on [currentPlaylistIndex,
  // currentSongIndex, isPlaying] and call a.load() unconditionally every
  // time — but .load() resets an <audio> element's playback position to
  // 0, so pausing (which only flips isPlaying, not the track) was
  // silently rewinding the track to the start every time. Only reload
  // when the actual track changes; play/pause just calls play()/pause()
  // on whatever's already loaded.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;

    if (currentSong.src && currentSong.src !== loadedSrcRef.current) {
      loadedSrcRef.current = currentSong.src;
      a.load();
      setProgress(0);
    }

    if (isPlaying) {
      a.play().catch(err => {
        console.warn('Playback failed:', err);
      });
    } else {
      a.pause();
    }
  }, [currentSong.src, isPlaying]);

  // Fetch cover art for whichever track is currently cued/playing. Track
  // metadata (title/artist/duration) comes from playlists.json instantly,
  // but artwork is pulled from the actual mp3's ID3 tag, which takes a
  // moment — show a spinner in the "now playing" art slot until it lands.
  const [currentArtwork, setCurrentArtwork] = useState(null);
  const [artworkLoading, setArtworkLoading] = useState(false);

  useEffect(() => {
    if (!currentSong.filename) {
      setCurrentArtwork(null);
      setArtworkLoading(false);
      return;
    }
    let cancelled = false;
    setArtworkLoading(true);
    setCurrentArtwork(null);
    loadTrackArtwork(currentSong.filename, currentSong.src).then((artwork) => {
      if (cancelled) return;
      setCurrentArtwork(artwork);
      setArtworkLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSong.filename]);

  return (
    <div className="music-player">
      <div className="content">
        <div className="song-info">
          <div className="album-art">
            {artworkLoading ? (
              <div className="album-art-loading" aria-label="Loading artwork">
                <div className="album-art-spinner" />
              </div>
            ) : (
              <img
                src={currentArtwork || defaultAlbumArt}
                alt="Album Art"
              />
            )}
          </div>
          <div className="song-details">
            <div className="song-title"><strong>{currentSong.title}</strong></div>
            <div className="artist-name">{currentSong.artist}</div>
            <div className="album-title">{currentSong.album}</div>
          </div>
        </div>

        <div
          className='visualizer-container'
          onClick={cycleVisualizer}
          title="Tap to change visualizer"
        >
          <canvas ref={visualizerCanvasRef} width={640} height={360} className="visualizer" />
        </div>

        <div className="playlists-panel">
          <div className="playlist-sidebar">
            <div className="playlist-sidebar-label">Playlists</div>
            {playlists.map((playlist, i) => (
              <div
                key={playlist.id}
                className={`playlist-sidebar-item ${i === viewedPlaylistIndex ? 'selected' : ''}`}
                onClick={() => setViewedPlaylistIndex(i)}
              >
                <svg className="playlist-sidebar-item-icon" width="11" height="11" viewBox="0 0 20 20" fill="currentColor"><path d="M7 4v9.2a3 3 0 1 0 2 2.83V8h6V4H7z" /></svg>
                <ScrollingLabel text={playlist.name} />
              </div>
            ))}
          </div>
          <ul className="playlist">
            {viewedTracks.map((track, i) => (
              <li
                key={track.id}
                className={
                  viewedPlaylistIndex === currentPlaylistIndex && i === currentSongIndex
                    ? 'active'
                    : ''
                }
                onClick={() => playTrackInViewedPlaylist(i)}
              >
                <ScrollingLabel className="track-name" text={track.artist ? `${track.title} - ${track.artist}` : track.title} />
                <span className="track-duration">{track.duration}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div
        className="progress-container"
        onClick={handleProgressClick}
      >
        <div
          className="progress-bar"
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      <div className="controls">
        <button onClick={playPrevious} className="control-button" aria-label="Previous">
          <FaStepBackward />
        </button>
        <button onClick={togglePlayPause} className="control-button" aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <FaPause /> : <FaPlay className="icon-play" />}
        </button>
        <button onClick={playNext} className="control-button" aria-label="Next">
          <FaStepForward />
        </button>
        <button
          onClick={() => setShuffle(s => !s)}
          className={`control-button shuffle ${shuffle ? 'on' : 'off'}`}
          title="Shuffle"
          aria-label="Shuffle"
        >
          <FaRandom />
        </button>
        <div className="volume-control">
          <FaVolumeDown className="vol-icon" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={e => setVolume(parseFloat(e.target.value))}
            className="volume-slider"
            aria-label="Volume"
            style={{
              background: `linear-gradient(to right, var(--player-volume-fill) 0%, var(--player-volume-fill) ${fill}, #999 ${fill}, #999 100%)`
            }}
          />
          <FaVolumeUp className="vol-icon" />
        </div>
      </div>

      <audio
        ref={audioRef}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
      >
        <source src={currentSong.src} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </div>
  );
};

export default MusicPlayer;
